import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Crown, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";

const FREE_FEATURES = ["Unlimited visual routines, timers & alarms", "1 talk board (20 tiles) & 3 choice boards", "Every Pro feature free for 1 hour, then a 6-hour rest", "Works on any device"];
const PREMIUM_FEATURES = ["All Pro features, no time limits", "Photos, AI helpers & care plans", "Multiple people profiles & Word exports", "Sync across all your devices"];
const ORG_FEATURES = ["Unlimited residents & staff", "Everything in Premium for the whole team", "Care notes with photos & audit trail", "One-click Word care records per client"];

export default function Premium() {
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [orgPlan, setOrgPlan] = useState(null);
  const [yearly, setYearly] = useState(false);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/payments/portal");
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.org_id) {
      api.get("/org").then((r) => setOrgPlan(r.data.plan)).catch(() => {});
    }
  }, [user?.org_id]);

  const upgrade = async (lookupKey) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const orgPaid = orgPlan === "business";

  return (
    <div data-testid="premium-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8 pb-24">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
          <Crown size={36} strokeWidth={2.5} className="text-[#B7791F]" /> RoutineBuddy plans
        </h1>
        <p className="mt-2 text-lg text-muted-foreground">Support the app and unlock everything. Cancel anytime.</p>

        <div className="mt-5 inline-flex rounded-full border-2 border-foreground/20 bg-white p-1" data-testid="billing-period-toggle">
          <button
            data-testid="billing-monthly"
            onClick={() => setYearly(false)}
            className={`min-h-[48px] px-6 rounded-full font-bold transition-colors duration-200 ${!yearly ? "bg-primary text-white" : "text-foreground"}`}
          >
            Monthly
          </button>
          <button
            data-testid="billing-yearly"
            onClick={() => setYearly(true)}
            className={`min-h-[48px] px-6 rounded-full font-bold transition-colors duration-200 ${yearly ? "bg-primary text-white" : "text-foreground"}`}
          >
            Yearly <span className="text-sm">(2 months free)</span>
          </button>
        </div>

        {isPremium && !user?.org_id ? (
          <div data-testid="already-premium-message" className="mt-8 bg-[#48BB78]/15 border-2 border-[#48BB78] rounded-3xl p-8 flex items-center gap-4">
            <Check size={40} strokeWidth={3} className="text-[#2F855A] shrink-0" />
            <div className="flex-1">
              <p className="text-2xl font-extrabold text-[#22543D]">You're Premium!</p>
              <p className="text-lg font-semibold text-[#22543D]">All features are unlocked on your account. Thank you for supporting RoutineBuddy.</p>
              <button
                data-testid="manage-subscription-button"
                onClick={openPortal}
                disabled={busy}
                className="mt-4 min-h-[52px] px-6 rounded-full bg-white border-2 border-[#48BB78] text-[#22543D] font-bold transition-colors duration-200 hover:bg-[#48BB78]/10 disabled:opacity-60"
              >
                {busy ? "Opening…" : "Manage or cancel subscription"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div data-testid="free-plan-card" className="bg-white rounded-3xl border-2 border-foreground/10 p-8">
              <h2 className="text-xl font-extrabold">Free</h2>
              <p className="mt-1 text-3xl font-extrabold">£0 <span className="text-base font-semibold text-muted-foreground">forever</span></p>
              <ul className="mt-5 space-y-3 text-lg">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> {f}</li>
                ))}
              </ul>
              <p className="mt-7 min-h-[56px] rounded-full border-2 border-foreground/15 font-bold text-lg flex items-center justify-center text-muted-foreground">Your current plan</p>
            </div>
            <div data-testid="premium-plan-card" className="bg-white rounded-3xl border-2 border-primary p-8">
              <h2 className="text-xl font-extrabold">Premium</h2>
              <p className="mt-1 text-3xl font-extrabold">{yearly ? "£50" : "£5"} <span className="text-base font-semibold text-muted-foreground">/ {yearly ? "year" : "month"}</span></p>
              <ul className="mt-5 space-y-3 text-lg">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f} className="flex gap-2"><Check size={24} className="text-primary shrink-0" strokeWidth={3} /> {f}</li>
                ))}
              </ul>
              <button
                data-testid="upgrade-button"
                onClick={() => upgrade(yearly ? "premium_yearly" : "premium_monthly")}
                disabled={busy}
                className="mt-7 w-full min-h-[56px] rounded-full bg-primary text-white font-bold text-lg flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60"
              >
                <Crown size={22} strokeWidth={2.5} /> {busy ? "Opening checkout…" : `Start 14-day free trial — then ${yearly ? "£50/year" : "£5/month"}`}
              </button>
              <p className="mt-3 text-sm text-muted-foreground text-center font-semibold">Secure payment via Stripe. Cancel anytime.</p>
            </div>
          </div>
        )}

        {user?.org_id && (
          <div data-testid="org-plan-card" className="mt-6 bg-white rounded-3xl border-2 border-[#4299E1] p-8">
            <h2 className="text-xl font-extrabold flex items-center gap-2"><Building2 size={26} strokeWidth={2.5} className="text-[#4299E1]" /> Organisation plan</h2>
            <p className="mt-1 text-3xl font-extrabold">{yearly ? "£150" : "£15"} <span className="text-base font-semibold text-muted-foreground">/ {yearly ? "year" : "month"} per organisation</span></p>
            <ul className="mt-5 space-y-3 text-lg">
              {ORG_FEATURES.map((f) => (
                <li key={f} className="flex gap-2"><Check size={24} className="text-[#4299E1] shrink-0" strokeWidth={3} /> {f}</li>
              ))}
            </ul>
            {orgPaid ? (
              <div>
                <p data-testid="org-paid-message" className="mt-7 min-h-[56px] rounded-full bg-[#48BB78]/15 border-2 border-[#48BB78] font-bold text-lg flex items-center justify-center gap-2 text-[#22543D]">
                  <Check size={24} strokeWidth={3} /> Your organisation is on this plan
                </p>
                {user.org_role === "owner" && (
                  <button
                    data-testid="org-manage-subscription-button"
                    onClick={openPortal}
                    disabled={busy}
                    className="mt-3 w-full min-h-[52px] rounded-full border-2 border-foreground/25 bg-white font-bold transition-colors duration-200 hover:bg-secondary disabled:opacity-60"
                  >
                    {busy ? "Opening…" : "Manage or cancel subscription"}
                  </button>
                )}
              </div>
            ) : user.org_role === "owner" ? (
              <button
                data-testid="org-upgrade-button"
                onClick={() => upgrade(yearly ? "business_yearly" : "business_monthly")}
                disabled={busy}
                className="mt-7 w-full min-h-[56px] rounded-full bg-[#4299E1] text-white font-bold text-lg flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60"
              >
                <Building2 size={22} strokeWidth={2.5} /> {busy ? "Opening checkout…" : `Start 30-day free trial — then ${yearly ? "£150/year" : "£15/month"}`}
              </button>
            ) : (
              <p className="mt-7 text-center font-semibold text-muted-foreground">Only the organisation owner can subscribe.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

